// ISS-041 (3 Nov 2026): a SELL OPTIONS trade's portfolio capital - original pairs + every rescue / Profit-Lock leg.
// Capital required = margin of everything held (Zerodha basket: SPAN + exposure) + net premium paid + charges + exit
// reserve, against the allocation less its buffer. The entry-time pair margin is shown as what it is, never as current.
import React from "react";

const rs = (v) => (v == null ? "—" : `${Number(v) < 0 ? "−" : ""}₹${Math.abs(Number(v)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
const SOURCE = { BROKER_BASKET: "Zerodha basket", SIMULATOR_BASKET: "Simulator basket", NOTHING_HELD: "nothing held", UNAVAILABLE: "unavailable" };

export function marginSourceText(c) {
  if (!c) return "—";
  const age = c.margin_at ? Math.max(0, Math.round(Date.now() / 1000 - c.margin_at)) : null;
  const src = SOURCE[c.margin_source] || c.margin_source || "—";
  return age == null ? src : `${src} · ${age < 120 ? `${age}s` : `${Math.round(age / 60)} min`} ago`;
}

export function CapitalLine({ capital, testId = "capital-line" }) {
  if (!capital || capital.error) return null;
  const over = capital.over_allocation;
  return (
    <span data-testid={testId} className={over ? "text-[#FF3B30] font-bold" : ""}
      title="Capital required (portfolio margin + net premium paid + charges + exit reserve) / usable allocation">
      capital {rs(capital.required)} / {rs(capital.usable)}{over ? ` · OVER ALLOCATION ${rs(over)}` : ""}
    </span>
  );
}

export default function CapitalBlock({ capital, testId = "capital-block" }) {
  if (!capital) return <div className="text-[var(--term-muted)] text-xs" data-testid={testId}>Capital: not computed yet</div>;
  if (capital.error) return <div className="text-[#FF3B30] text-xs" data-testid={testId}>Capital: {capital.error}</div>;
  const est = capital.margin_source !== "BROKER_BASKET" && capital.margin_source !== "NOTHING_HELD";
  const rows = [
    ["Allocated funds", rs(capital.allocated)],
    ["Margin buffer", `${rs(capital.reserve)} (${capital.reserve_pct}%)`],
    ["Usable allocation", rs(capital.usable)],
    ["Original pair margin (at entry)", rs(capital.original_pair_margin)],
    ["Portfolio margin now", capital.margin_now == null ? "unavailable" : rs(capital.margin_now), est ? "text-amber-600" : ""],
    ["Margin source", marginSourceText(capital), est ? "text-amber-600" : ""],
    ["Net premium paid (+) / received (−)", rs(capital.net_premium_paid)],
    ["Rescue BUY / SELL premium", `${rs(capital.rescue_buy_premium)} / ${rs(capital.rescue_sell_premium)}`],
    ["Charges · exit reserve", `${rs(capital.charges)} · ${rs(capital.exit_reserve)}`],
    ["Capital required", rs(capital.required), "font-bold"],
    ["Peak capital", rs(capital.peak)],
    ["Remaining allocation", rs(capital.remaining), capital.remaining != null && capital.remaining < 0 ? "text-[#FF3B30] font-bold" : ""],
  ];
  return (
    <div data-testid={testId} className="text-xs space-y-0.5">
      {capital.over_allocation ? (
        <div className="rounded-sm border border-[#FF3B30]/50 px-2 py-1 text-[#FF3B30] font-bold" data-testid={`${testId}-over`}>
          OVER ALLOCATION by {rs(capital.over_allocation)} — new capital-increasing entries and rescue adjustments are refused;
          risk-reducing orders, hedge completion and Square Off stay available.
        </div>) : null}
      {rows.map(([k, v, cls]) => (
        <div key={k} className="flex justify-between gap-2"><span className="text-[var(--term-muted)]">{k}</span><span className={`mono ${cls || ""}`}>{v}</span></div>
      ))}
      <div className="text-[10px] text-[var(--term-muted)]">
        Capital required = portfolio margin (SPAN + exposure of every held contract) + net premium paid + charges + exit reserve.
        {est ? " Margin is not a Zerodha figure right now." : ""} SHADOW figures are simulated, not funds blocked at Zerodha.
      </div>
    </div>
  );
}
